import { useEffect, useState } from "react";
import { HiStar } from "react-icons/hi";
import { HiOutlineBookOpen, HiOutlineUsers } from "react-icons/hi2";
import { Link } from "react-router-dom";
import Badge from "../../components/Badge";
import Button from "../../components/Button";
import Card from "../../components/Card";
import FilterPanel from "../../components/FilterPanel";
import LoadingSpinner from "../../components/LoadingSpinner";
import PageHeader from "../../components/PageHeader";
import Select from "../../components/Select";
import StarRating from "../../components/StarRating";
import { api } from "../../services/api";

function BrowseTutors() {
  const [tutors, setTutors] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [filters, setFilters] = useState({
    subject: "",
    rating: "",
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadSubjects();
  }, []);

  useEffect(() => {
    loadTutors();
  }, [filters]);

  const loadSubjects = async () => {
    try {
      const response = await api.get("/subjects");
      setSubjects(response.subjects || []);
    } catch (error) {
      console.error("Error loading subjects:", error);
    }
  };

  const loadTutors = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filters.subject) params.append("subject", filters.subject);
      if (filters.rating) params.append("rating", filters.rating);

      const response = await api.get(`/tutors?${params.toString()}`);
      setTutors(response.tutors || []);
    } catch (error) {
      console.error("Error loading tutors:", error);
    } finally {
      setLoading(false);
    }
  };

  // Generate avatar initials
  const getInitials = (firstName, lastName) => {
    return `${firstName?.charAt(0) || ""}${
      lastName?.charAt(0) || ""
    }`.toUpperCase();
  };

  // Generate color based on name
  const getAvatarColor = (name) => {
    const colors = [
      "bg-primary",
      "bg-secondary",
      "bg-accent",
      "bg-primary/80",
      "bg-secondary/80",
      "bg-accent/80",
      "bg-primary/60",
      "bg-secondary/60",
    ];
    const index = (name?.charCodeAt(0) || 0) % colors.length;
    return colors[index];
  };

  const subjectOptions = [
    { value: "", label: "All Subjects" },
    ...subjects.map((subject) => ({
      value: subject._id,
      label: `${subject.name} (${subject.code})`,
    })),
  ];

  const ratingOptions = [
    { value: "", label: "Any Rating" },
    { value: "4", label: "4+ Stars" },
    { value: "3", label: "3+ Stars" },
    { value: "2", label: "2+ Stars" },
  ];

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <PageHeader
        title="Browse Tutors"
        subtitle="Find the perfect tutor for your learning needs"
      />

      {/* Filters */}
      <FilterPanel>
        <Select
          label={
            <span className="flex items-center gap-2">
              <HiOutlineBookOpen className="w-4 h-4" />
              Subject
            </span>
          }
          value={filters.subject}
          onChange={(e) => setFilters({ ...filters, subject: e.target.value })}
          options={subjectOptions}
        />
        <Select
          label={
            <span className="flex items-center gap-2">
              <HiStar className="w-4 h-4" />
              Minimum Rating
            </span>
          }
          value={filters.rating}
          onChange={(e) => setFilters({ ...filters, rating: e.target.value })}
          options={ratingOptions}
        />
      </FilterPanel>

      {/* Results */}
      {loading ? (
        <LoadingSpinner size="lg" className="py-16" />
      ) : tutors.length === 0 ? (
        <div className="text-center py-16">
          <HiOutlineUsers className="mx-auto h-16 w-16 text-text/40 mb-4" />
          <h3 className="text-lg font-medium text-text mb-2">
            No tutors found
          </h3>
          <p className="text-text/70">
            Try adjusting your filters to see more results
          </p>
        </div>
      ) : (
        <>
          <div className="mb-4 text-sm text-text/70">
            Found {tutors.length} tutor{tutors.length !== 1 ? "s" : ""}
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {tutors.map((tutor) => (
              <Card
                key={tutor._id}
                className="hover:shadow-xl transition-all duration-300 hover:-translate-y-1"
              >
                {/* Avatar and Name */}
                <div className="flex items-center mb-4">
                  <div
                    className={`w-16 h-16 rounded-full ${getAvatarColor(
                      tutor.name?.first
                    )} flex items-center justify-center text-white text-xl font-bold mr-4 flex-shrink-0`}
                  >
                    {getInitials(tutor.name?.first, tutor.name?.last)}
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="text-xl font-semibold text-text truncate">
                      {tutor.name?.first} {tutor.name?.last}
                    </h3>
                    <p className="text-sm text-text/70">{tutor.email}</p>
                  </div>
                </div>

                {/* Bio */}
                <p className="text-sm text-text/80 mb-4 line-clamp-3">
                  {tutor.profile?.bio ? (
                    tutor.profile.bio
                  ) : (
                    <span className="italic text-text/60">
                      No bio available
                    </span>
                  )}
                </p>

                {/* Rating */}
                <div className="mb-4">
                  <StarRating value={tutor.profile?.rating || 0} readonly />
                </div>

                {/* Subjects */}
                {tutor.profile?.subjects &&
                  tutor.profile.subjects.length > 0 && (
                    <div className="mb-4">
                      <p className="text-xs font-semibold text-text/70 uppercase tracking-wide mb-2">
                        Subjects
                      </p>
                      <div className="flex flex-wrap gap-2">
                        {tutor.profile.subjects.map((subj, idx) => (
                          <Badge
                            key={idx}
                            label={subj.subjectId?.name || "Unknown"}
                            variant="primary"
                            size="small"
                            className="capitalize font-medium"
                          />
                        ))}
                      </div>
                    </div>
                  )}

                {/* View Profile Button */}
                <Link to={`/tutors/${tutor._id}`} className="block w-full">
                  <Button className="w-full">View Profile</Button>
                </Link>
              </Card>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

export default BrowseTutors;
